import { KONTRAKTY_ZDARZEN, readEventPayload } from '@insolitum/types';

import { type NatsCategory, type ParsedNatsSubject } from './natsSubject';

/**
 * Czysta warstwa prezentacji zdarzeń NATS: etykiety kategorii, kolory oraz
 * budowa polskiego komunikatu.
 *
 * DLACZEGO osobno od `hooks/useNatsActivity`: hook ciągnie za sobą
 * `WebSocketProvider` → `lib/supabase`, który przy imporcie wymaga zmiennych
 * środowiskowych. Formatery są czystymi funkcjami i muszą dać się testować bez
 * tej całej maszynerii (ten sam podział co `nexusApi` / `nexusApiCore`).
 */

// Get human-readable label for category
export function getCategoryLabel(category: NatsCategory): string {
  const labels: Record<NatsCategory, string> = {
    telemetry: 'Telemetria',
    alerts: 'Alerty',
    events: 'Zdarzenia',
    modules: 'Moduły',
    system: 'System',
    unknown: 'Inne',
  };
  return labels[category];
}

// Get color scheme for category
export function getCategoryColor(category: NatsCategory): {
  bg: string;
  text: string;
  dot: string;
} {
  const colors: Record<NatsCategory, { bg: string; text: string; dot: string }> = {
    telemetry: { bg: 'bg-blue-500/20', text: 'text-blue-400', dot: 'bg-blue-500' },
    alerts: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-500' },
    events: { bg: 'bg-green-500/20', text: 'text-green-400', dot: 'bg-green-500' },
    modules: { bg: 'bg-purple-500/20', text: 'text-purple-400', dot: 'bg-purple-500' },
    system: { bg: 'bg-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-500' },
    unknown: { bg: 'bg-gray-500/20', text: 'text-gray-400', dot: 'bg-gray-500' },
  };
  return colors[category];
}

// Format action to be more readable
export function formatAction(action: string): string {
  return action
    .replace(/_/g, ' ')
    .replace(/\./g, ' → ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

// Format source name
export function formatSource(source: string): string {
  // Map known sources to friendly names
  const sourceMap: Record<string, string> = {
    lexshift: 'LexShift',
    waterandice: 'Temperatio',
    neurosense: 'NeuroSense',
    'websocket-gateway': 'Gateway',
    gateway: 'Gateway',
    'ingestion-worker': 'Ingestion',
    system: 'System',
    temperatio: 'Temperatio',
    aegis: 'Aegis',
    agenda: 'Agenda',
    provisio: 'Provisio',
    gamification: 'Aegis',
    artifex: 'Artifex',
    // Organ wzroku publikuje pod `nexus_eye` (tak nazywają się tabele i subjecty),
    // ale człowiek w kanale zdarzeń zna go pod nazwą modułu.
    nexus_eye: 'Conspectus',
    conspectus: 'Conspectus',
    // Rodziny z packages/types/src/nats.ts, których do 13.09.2026 nie było
    // w mapie — feed pokazywał wtedy surowy segment tematu (`health`).
    health: 'Zdrowie platformy',
    'nexus-core': 'NEXUS',
    nexus: 'NEXUS',
    fabrica: 'Fabrica',
    ucs: 'UCS',
    presence: 'Obecność',
    memory: 'Pamięć NEXUS',
    platform: 'Platforma',
    code: 'Kod',
    steering: 'Sterowanie',
    actuation: 'Wykonanie',
    print: 'Drukowanie',
    display: 'Ekrany',
    render: 'Render',
    genesis: 'Genesis',
    success: 'Success',
    openclaw: 'OpenClaw',
    robot: 'Robot',
    timed_task: 'Zadania czasowe',
  };

  return sourceMap[source.toLowerCase()] || source;
}

// ─── Sygnały urządzeń po polsku ─────────────────────────────────────────────
//
// Klucze to słownik adaptera Shelly (services/neurosense-gateway-mqtt/src/
// shellyAdapter.ts, GEN1_METRIC_MAP / GEN3_COMPONENT_MAP) — jedyne źródło nazw
// sygnałów na magistrali. Jednostki `bool` i `level` są tam OPISEM TYPU, nie
// tekstem dla człowieka: do 13.09.2026 feed sklejał `${value}${unit}` i pisał
// „(online): 0bool". Sygnał logiczny ma dwa stany nazwane po polsku.

interface OpisSygnalu {
  etykieta: string;
  jednostka?: string;
  /** [gdy prawda, gdy fałsz] — sygnał jest logiczny, liczby nie pokazujemy. */
  logiczny?: [string, string];
  ikona?: string;
  ikonaFalsz?: string;
}

export const SYGNALY: Record<string, OpisSygnalu> = {
  temperature: { etykieta: 'temperatura', jednostka: '°C', ikona: 'thermometer' },
  humidity: { etykieta: 'wilgotność', jednostka: '%', ikona: 'droplets' },
  battery: { etykieta: 'bateria', jednostka: '%', ikona: 'battery' },
  online: {
    etykieta: '',
    logiczny: ['dostępny', 'niedostępny'],
    ikona: 'wifi',
    ikonaFalsz: 'wifi-off',
  },
  error: {
    etykieta: 'błąd urządzenia',
    logiczny: ['zgłoszony', 'brak'],
    ikona: 'alert-circle',
    ikonaFalsz: 'check-circle',
  },
  rssi: { etykieta: 'sygnał Wi‑Fi', jednostka: 'dBm', ikona: 'wifi' },
  power: { etykieta: 'moc', jednostka: 'W', ikona: 'zap' },
  power_total: { etykieta: 'moc całkowita', jednostka: 'W', ikona: 'zap' },
  energy_total: { etykieta: 'energia', jednostka: 'Wh', ikona: 'zap' },
  voltmeter: { etykieta: 'napięcie', jednostka: 'V', ikona: 'zap' },
  gas_ppm: { etykieta: 'stężenie gazu', jednostka: 'ppm', ikona: 'wind' },
  gas_alarm: { etykieta: 'alarm gazu', ikona: 'alert-triangle' },
  smoke_alarm: {
    etykieta: 'dym',
    logiczny: ['wykryto', 'brak'],
    ikona: 'flame',
    ikonaFalsz: 'check-circle',
  },
  flood_alarm: {
    etykieta: 'zalanie',
    logiczny: ['wykryto', 'brak'],
    ikona: 'droplets',
    ikonaFalsz: 'check-circle',
  },
  input: {
    etykieta: 'wejście',
    logiczny: ['aktywne', 'nieaktywne'],
    ikona: 'toggle-right',
    ikonaFalsz: 'toggle-left',
  },
  relay: {
    etykieta: 'przekaźnik',
    logiczny: ['włączony', 'wyłączony'],
    ikona: 'toggle-right',
    ikonaFalsz: 'toggle-left',
  },
  door: {
    etykieta: 'drzwi',
    logiczny: ['otwarte', 'zamknięte'],
    ikona: 'door-open',
    ikonaFalsz: 'door-closed',
  },
  open: {
    etykieta: 'drzwi',
    logiczny: ['otwarte', 'zamknięte'],
    ikona: 'door-open',
    ikonaFalsz: 'door-closed',
  },
};

/** Jednostki, które są typem danych, a nie tekstem — nigdy nie trafiają do zdania. */
const JEDNOSTKI_TYPU = new Set(['bool', 'boolean', 'level', 'enum', 'state']);
/** Jednostki pisane przy liczbie, bez spacji (`4,2°C`, `53,1%`). */
const JEDNOSTKI_PRZY_LICZBIE = new Set(['°C', '°F', '%']);

export function czyPrawda(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true' ||
    value === 'on' ||
    value === 'online' ||
    value === 'open'
  );
}

/** Liczba po polsku: przecinek dziesiętny, najwyżej dwa miejsca. */
export function formatujLiczbe(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (typeof value !== 'boolean' && Number.isFinite(n) && String(value).trim() !== '') {
    return n.toLocaleString('pl-PL', { maximumFractionDigits: 2 });
  }
  return String(value ?? '—');
}

export function zJednostka(value: unknown, unit: unknown): string {
  const liczba = formatujLiczbe(value);
  const j = typeof unit === 'string' ? unit.trim() : '';
  if (!j || JEDNOSTKI_TYPU.has(j.toLowerCase())) return liczba;
  return JEDNOSTKI_PRZY_LICZBIE.has(j) ? `${liczba}${j}` : `${liczba} ${j}`;
}

/**
 * Odczyt sygnału jako fraza: „temperatura 4,2°C", „dostępny", „dym: wykryto",
 * „alarm gazu: poziom mild". Nieznany sygnał dostaje nazwę z podkreśleń
 * zamienionych na spacje i wartość z jednostką (o ile jednostka jest tekstem).
 */
export function opiszOdczyt(
  signal: string | undefined,
  value: unknown,
  unit: unknown
): { fraza: string; ikona?: string } {
  const klucz = signal ?? 'reading';
  const opis = SYGNALY[klucz];
  const logiczny =
    opis?.logiczny ??
    (typeof unit === 'string' && ['bool', 'boolean'].includes(unit.toLowerCase())
      ? (['tak', 'nie'] as [string, string])
      : undefined);

  if (logiczny) {
    const prawda = czyPrawda(value);
    const stan = prawda ? logiczny[0] : logiczny[1];
    const etykieta = opis?.etykieta ?? klucz.replace(/_/g, ' ');
    return {
      fraza: etykieta ? `${etykieta}: ${stan}` : stan,
      ikona: prawda ? opis?.ikona : (opis?.ikonaFalsz ?? opis?.ikona),
    };
  }

  if (typeof unit === 'string' && unit.toLowerCase() === 'level') {
    return {
      fraza: `${opis?.etykieta ?? klucz.replace(/_/g, ' ')}: poziom ${value ?? '—'}`,
      ikona: opis?.ikona,
    };
  }

  const etykieta = opis?.etykieta ?? (klucz === 'reading' ? 'odczyt' : klucz.replace(/_/g, ' '));
  const wartosc =
    value === undefined || value === null ? '—' : zJednostka(value, unit ?? opis?.jednostka);
  return { fraza: `${etykieta} ${wartosc}`, ikona: opis?.ikona ?? 'activity' };
}

/** Kontekst czytelnika — to, czego w temacie i ładunku nie ma (kartoteka nazw). */
export interface KontekstZdarzenia {
  nazwaUrzadzenia?: (deviceId: string) => string | undefined;
}

function nazwaCzujnika(deviceId: string, ctx?: KontekstZdarzenia): string {
  return ctx?.nazwaUrzadzenia?.(deviceId) ?? `Czujnik ${deviceId}`;
}

/**
 * Rytm serca platformy (`health.heartbeat`) leci co przebieg sond,
 * z `perspective: 'super_admin'`; dla administratora firmy nie niesie żadnej
 * informacji, a wypycha prawdziwe zdarzenia z 50-elementowego bufora.
 * Odsiewany PRZED dodaniem do listy. `degraded`/`recovered` zostają.
 */
export function czyDlaCzlowieka(parsed: Pick<ParsedNatsSubject, 'source' | 'action'>): boolean {
  return !(parsed.source === 'health' && parsed.action === 'heartbeat');
}

/**
 * Zdanie o źródle pod tytułem — zamiast surowego tematu NATS, który schodzi do
 * rozwinięcia wiersza. „NeuroSense · czujnik CHŁODNIA-PIEKARNIA",
 * „LexShift · zdarzenie", „Zdrowie platformy · system".
 */
export function opiszZrodlo(parsed: ParsedNatsSubject, ctx?: KontekstZdarzenia): string {
  const { category, source, deviceId } = parsed;
  if (deviceId) {
    const nazwa = ctx?.nazwaUrzadzenia?.(deviceId);
    return `NeuroSense · czujnik ${nazwa ? `${nazwa} (${deviceId})` : deviceId}`;
  }
  const rodzaj: Record<NatsCategory, string> = {
    telemetry: 'telemetria',
    alerts: 'alert',
    events: 'zdarzenie',
    modules: 'moduł',
    system: 'system',
    unknown: 'zdarzenie',
  };
  return `${formatSource(source)} · ${rodzaj[category]}`;
}

/** Pierwsze zdanie `opis` z kontraktu zdarzenia — tytuł zapasowy dla `{module}.{event}`. */
export function tytulZKontraktu(source: string, action: string): string | undefined {
  const opis = KONTRAKTY_ZDARZEN[`${source}.${action}`]?.opis;
  if (!opis) return undefined;
  const pierwsze = opis
    .split(/\s+→\s+|\s+\(/)[0]
    ?.trim()
    .replace(/[.:]$/, '');
  return pierwsze ? pierwsze[0].toUpperCase() + pierwsze.slice(1) : undefined;
}

// Helper to safely get nested properties from data. Envelope-aware: Envelope v2
// events carry domain fields under `payload`, so flatten first (readEventPayload
// leaves bare legacy payloads untouched) — otherwise device_id/value/etc. read
// as undefined once a module switches to Envelope v2 and the feed goes blank.
function getDataValue(data: unknown, key: string): string | number | undefined {
  const d = readEventPayload(data);
  if (key in d) {
    return d[key] as string | number;
  }
  return undefined;
}

/**
 * To samo, ale o jeden poziom głębiej — dla ładunków, które niosą treść
 * w zagnieżdżonym obiekcie (`{ subject, command: {...}, szacunek: {...} }`).
 *
 * ARTIFEX publikuje tak dwa zdarzenia sterujące, bo ich ładunkiem JEST
 * polecenie wysłane na magistralę; `getDataValue` czyta wyłącznie wierzchni
 * poziom i zwróciłby dla nich `undefined` na wszystkim, co warto pokazać.
 */
function getNestedValue(
  data: unknown,
  container: string,
  key: string
): string | number | undefined {
  const inner = readEventPayload(data)[container];
  if (inner && typeof inner === 'object') {
    const v = (inner as Record<string, unknown>)[key];
    if (typeof v === 'string' || typeof v === 'number') return v;
  }
  return undefined;
}

/**
 * Buduje komunikat po polsku dla pojedynczego zdarzenia.
 *
 * Przyjmuje CAŁY sparsowany subject, a nie samo `source`/`action`, bo telemetria
 * niesie `deviceId` i `signal` wyłącznie w subjeccie — wcześniejsza sygnatura
 * `(source, action, data)` nie miała ich skąd wziąć i komunikat wychodził jako
 * „Odczyt z device: undefined".
 */
export function formatEventMessage(
  parsed: ParsedNatsSubject,
  data: unknown,
  ctx?: KontekstZdarzenia
): { message: string; icon?: string } {
  const { source, action, category, deviceId, signal } = parsed;
  const d = data as Record<string, unknown> | undefined;

  // Telemetria / status / bateria — identyfikuje urządzenie (po nazwie
  // z kartoteki, gdy czytelnik ją zna), nie moduł.
  if (category === 'telemetry' && deviceId) {
    const kto = nazwaCzujnika(deviceId, ctx);
    if (signal === 'status') {
      const status =
        getDataValue(d, 'status') ?? getDataValue(d, 'state') ?? getDataValue(d, 'value');
      const online = czyPrawda(status);
      const znany = online || status === 'offline' || status === 0 || status === '0';
      return {
        message: `${kto}: ${znany ? (online ? 'dostępny' : 'niedostępny') : (status ?? 'zmiana statusu')}`,
        icon: online ? 'wifi' : 'wifi-off',
      };
    }
    if (signal === 'battery') {
      const level = getDataValue(d, 'battery') ?? getDataValue(d, 'value');
      return {
        message: `${kto}: bateria ${level === undefined ? '—' : `${formatujLiczbe(level)}%`}`,
        icon: 'battery',
      };
    }
    const value = getDataValue(d, 'value');
    const unit = getDataValue(d, 'unit');
    const { fraza, ikona } = opiszOdczyt(signal, value, unit);
    return { message: `${kto}: ${fraza}`, icon: ikona };
  }

  // Zdrowie platformy (services/nexus-core/src/health/HealthEngine.ts) —
  // ładunek: { component, signal_type, status, value: { probes, at } }.
  if (source === 'health') {
    const component = getDataValue(d, 'component');
    const status = getDataValue(d, 'status');
    switch (action) {
      case 'heartbeat': {
        const probes = getNestedValue(d, 'value', 'probes');
        return {
          message: `Rytm serca platformy: ${
            probes !== undefined ? `${probes} sond, ` : ''
          }stan ${status === 'ok' ? 'w porządku' : (status ?? 'nieznany')}`,
          icon: 'heart-pulse',
        };
      }
      case 'degraded':
        return {
          message: `Usługa ${component ?? 'platformy'} działa z ograniczeniami`,
          icon: 'alert-triangle',
        };
      case 'recovered':
        return {
          message: `Usługa ${component ?? 'platformy'} wróciła do sprawności`,
          icon: 'check-circle',
        };
      case 'kill_switch_engaged':
        return { message: `Zatrzymano awaryjnie: ${component ?? 'platforma'}`, icon: 'octagon' };
      case 'kill_switch_released':
        return {
          message: `Zdjęto zatrzymanie awaryjne: ${component ?? 'platforma'}`,
          icon: 'play',
        };
      default:
        return { message: `Zdrowie platformy: ${formatAction(action)}`, icon: 'heart-pulse' };
    }
  }

  // System events
  if (source === 'gateway') {
    if (action === 'client_connected') {
      return {
        message: 'Nowe połączenie z systemem',
        icon: 'log-in',
      };
    }
    if (action === 'client_disconnected') {
      return {
        message: 'Rozłączono z systemem',
        icon: 'log-out',
      };
    }
  }

  // LexShift - HR/Scheduling module
  if (source === 'lexshift') {
    const employee = getDataValue(d, 'employee') || getDataValue(d, 'employee_name') || 'Pracownik';
    const time = getDataValue(d, 'time');

    switch (action) {
      case 'clock_in':
      case 'employee_clocked_in':
        return {
          message: `${employee} rozpoczął pracę${time ? ` o ${time}` : ''}`,
          icon: 'log-in',
        };
      case 'clock_out':
      case 'employee_clocked_out':
        return {
          message: `${employee} zakończył pracę${time ? ` o ${time}` : ''}`,
          icon: 'log-out',
        };
      case 'break_started':
        return {
          message: `${employee} rozpoczął przerwę`,
          icon: 'coffee',
        };
      case 'break_ended':
        return {
          message: `${employee} zakończył przerwę`,
          icon: 'play',
        };
      case 'schedule_created':
      case 'schedule_updated': {
        const week = getDataValue(d, 'week');
        return {
          message: `Zaktualizowano grafik${week ? ` na tydzień ${week}` : ''}`,
          icon: 'calendar',
        };
      }
      case 'schedule_published':
        return {
          message: 'Opublikowano nowy grafik pracy',
          icon: 'send',
        };
      case 'leave_requested': {
        const days = getDataValue(d, 'days');
        return {
          message: `${employee} złożył wniosek urlopowy${days ? ` (${days} dni)` : ''}`,
          icon: 'calendar-plus',
        };
      }
      case 'leave_approved':
        return {
          message: `Zatwierdzono urlop dla ${employee}${getDataValue(d, 'days') ? ` (${getDataValue(d, 'days')} dni)` : ''}`,
          icon: 'check-circle',
        };
      case 'leave_rejected':
        return {
          message: `Odrzucono wniosek urlopowy ${employee}`,
          icon: 'x-circle',
        };
    }
  }

  // Temperatio (źródło `waterandice`) - Bakery temperature monitoring
  if (source === 'waterandice') {
    switch (action) {
      case 'temperature_recorded':
      case 'measurement': {
        const temp = getDataValue(d, 'temp') || getDataValue(d, 'temperature');
        const dough = getDataValue(d, 'dough');
        if (dough) {
          return {
            message: `Pomiar temperatury: ${dough} - ${temp}°C`,
            icon: 'thermometer',
          };
        }
        return {
          message: `Zarejestrowano temperaturę: ${temp}°C`,
          icon: 'thermometer',
        };
      }
      case 'temperature_alert':
        return {
          message: `⚠️ Alert temperatury: ${getDataValue(d, 'value')}°C (próg: ${getDataValue(d, 'threshold')}°C)`,
          icon: 'alert-triangle',
        };
      case 'dough_started':
        return {
          message: `Rozpoczęto przygotowanie ciasta: ${getDataValue(d, 'dough') || getDataValue(d, 'name')}`,
          icon: 'play-circle',
        };
      case 'dough_ready':
        return {
          message: `Ciasto gotowe: ${getDataValue(d, 'dough') || getDataValue(d, 'name')}`,
          icon: 'check-circle',
        };
      case 'dough_status_changed':
        return {
          message: `Zmiana statusu ciasta: ${getDataValue(d, 'status')}`,
          icon: 'refresh-cw',
        };
    }
  }

  // NeuroSense - IoT sensors
  if (source === 'neurosense') {
    const idCzujnika = String(getDataValue(d, 'device_id') || getDataValue(d, 'device') || '');
    const nazwaZLadunku = getDataValue(d, 'device_name');
    const czujnik = idCzujnika
      ? (typeof nazwaZLadunku === 'string' && nazwaZLadunku) || nazwaCzujnika(idCzujnika, ctx)
      : 'Czujnik';
    switch (action) {
      case 'device_online':
      case 'sensor_online':
        return {
          message: `${czujnik} jest dostępny`,
          icon: 'wifi',
        };
      case 'sensor_offline':
        return {
          message: `${czujnik} przestał odpowiadać`,
          icon: 'wifi-off',
        };
      case 'sensor_reading':
        return {
          message: `Odczyt: ${czujnik}`,
          icon: 'activity',
        };
      case 'sensor_alert': {
        const rodzaj = getDataValue(d, 'alert_type');
        return {
          message: `⚠️ Alert z czujnika ${czujnik}${rodzaj ? `: ${String(rodzaj).replace(/_/g, ' ')}` : ''}`,
          icon: 'alert-triangle',
        };
      }
      case 'area_occupancy_changed':
        return {
          message: `Zmiana obecności w strefie: ${getDataValue(d, 'area')}`,
          icon: 'users',
        };
    }
  }

  // Temperatio - Temperature monitoring
  if (source === 'temperatio') {
    switch (action) {
      case 'temperature_recorded': {
        const location = getDataValue(d, 'location');
        const tempValue = getDataValue(d, 'temp') || getDataValue(d, 'temperature');
        return {
          message: `Pomiar temperatury${location ? ` w ${location}` : ''}: ${tempValue}°C`,
          icon: 'thermometer',
        };
      }
      case 'temperature_alert':
        return {
          message: `⚠️ Alert temperatury: ${getDataValue(d, 'location')}`,
          icon: 'alert-triangle',
        };
    }
  }

  // Aegis - Access control
  if (source === 'aegis') {
    const employeeName = getDataValue(d, 'employee') || getDataValue(d, 'user');
    const zone = getDataValue(d, 'zone') || getDataValue(d, 'area');
    switch (action) {
      case 'access_granted':
        return {
          message: `${employeeName} uzyskał dostęp do strefy ${zone}`,
          icon: 'unlock',
        };
      case 'access_denied':
        return {
          message: `Odmowa dostępu: ${employeeName} do strefy ${zone}`,
          icon: 'lock',
        };
      case 'door_opened':
        return {
          message: `Otwarto drzwi: ${zone}`,
          icon: 'door-open',
        };
      case 'door_closed':
        return {
          message: `Zamknięto drzwi: ${zone}`,
          icon: 'door-closed',
        };
    }
  }

  // Agenda - Task management
  if (source === 'agenda') {
    const title = getDataValue(d, 'title') || getDataValue(d, 'task');
    const assignee = getDataValue(d, 'assignee');
    switch (action) {
      case 'task_created':
        return {
          message: `Utworzono zadanie: ${title}${assignee ? ` (${assignee})` : ''}`,
          icon: 'plus-circle',
        };
      case 'task_updated':
        return {
          message: `Zaktualizowano zadanie: ${title}`,
          icon: 'edit',
        };
      case 'task_deleted':
        return {
          message: `Usunięto zadanie: ${title}`,
          icon: 'trash',
        };
      case 'task_assigned':
        return {
          message: `Przypisano zadanie ${title} do ${assignee}`,
          icon: 'user-plus',
        };
      case 'task_started':
        return {
          message: `Rozpoczęto zadanie: ${title}`,
          icon: 'play',
        };
      case 'task_completed': {
        const duration = getDataValue(d, 'duration_minutes');
        return {
          message: `Ukończono zadanie: ${title}${duration ? ` (${duration} min)` : ''}`,
          icon: 'check-circle',
        };
      }
      case 'task_overdue':
        return {
          message: `⚠️ Zadanie zaległe: ${title}`,
          icon: 'alert-circle',
        };
      case 'schedule_created':
        return {
          message: 'Utworzono nowy harmonogram zadań',
          icon: 'calendar-plus',
        };
      case 'schedule_triggered':
        return {
          message: `Uruchomiono zaplanowane zadanie: ${title}`,
          icon: 'clock',
        };
      case 'group_created':
        return {
          message: `Utworzono grupę: ${getDataValue(d, 'name')}`,
          icon: 'users',
        };
      case 'group_member_added':
        return {
          message: `Dodano członka do grupy`,
          icon: 'user-plus',
        };
      case 'group_member_removed':
        return {
          message: `Usunięto członka z grupy`,
          icon: 'user-minus',
        };
    }
  }

  // Provisio - Procurement & Inventory
  if (source === 'provisio') {
    const product = getDataValue(d, 'product') || getDataValue(d, 'item');
    const supplier = getDataValue(d, 'supplier');
    switch (action) {
      case 'inventory_created':
      case 'inventory_updated':
        return {
          message: `Zaktualizowano stan magazynowy: ${product}`,
          icon: 'package',
        };
      case 'low_stock_alert':
        return {
          message: `⚠️ Niski stan magazynowy: ${product} (${getDataValue(d, 'current')}/${getDataValue(d, 'min')})`,
          icon: 'alert-triangle',
        };
      case 'out_of_stock_alert':
        return {
          message: `🚨 Brak na stanie: ${product}`,
          icon: 'alert-octagon',
        };
      case 'order_created': {
        const items = getDataValue(d, 'items');
        return {
          message: `Utworzono zamówienie${supplier ? ` u ${supplier}` : ''}${items ? ` (${items} pozycji)` : ''}`,
          icon: 'shopping-cart',
        };
      }
      case 'order_sent':
        return {
          message: `Wysłano zamówienie do ${supplier}`,
          icon: 'send',
        };
      case 'order_confirmed':
        return {
          message: `Potwierdzono zamówienie od ${supplier}`,
          icon: 'check',
        };
      case 'order_delivered':
        return {
          message: `Dostawa zrealizowana od ${supplier}`,
          icon: 'truck',
        };
      case 'order_cancelled':
        return {
          message: `Anulowano zamówienie`,
          icon: 'x-circle',
        };
      case 'invoice_uploaded':
        return {
          message: `Dodano fakturę od ${supplier}`,
          icon: 'file-plus',
        };
      case 'invoice_processed':
        return {
          message: `Przetworzono fakturę`,
          icon: 'file-check',
        };
      case 'invoice_paid':
        return {
          message: `Opłacono fakturę`,
          icon: 'credit-card',
        };
      case 'stock_received':
        return {
          message: `Przyjęto towar: ${product}`,
          icon: 'package-check',
        };
      case 'stock_adjusted':
        return {
          message: `Skorygowano stan: ${product}`,
          icon: 'edit-3',
        };
      case 'mrp_proposal_generated':
        return {
          message: 'Wygenerowano propozycję zamówień MRP',
          icon: 'cpu',
        };
      case 'mrp_order_created':
        return {
          message: 'Utworzono zamówienie z MRP',
          icon: 'shopping-bag',
        };
    }
  }

  // Aegis (źródło `gamification`) - punkty, osiągnięcia, poziomy
  if (source === 'gamification') {
    const employee = getDataValue(d, 'employee') || getDataValue(d, 'user');
    switch (action) {
      case 'achievement_earned':
        return {
          message: `🏆 ${employee} zdobył osiągnięcie: ${getDataValue(d, 'achievement')}`,
          icon: 'award',
        };
      case 'achievement_progress':
        return {
          message: `Postęp w osiągnięciu: ${getDataValue(d, 'achievement')}`,
          icon: 'trending-up',
        };
      case 'level_up':
        return {
          message: `🎉 ${employee} awansował na poziom ${getDataValue(d, 'level')}!`,
          icon: 'arrow-up-circle',
        };
      case 'points_awarded':
        return {
          message: `${employee} otrzymał ${getDataValue(d, 'points')} punktów`,
          icon: 'star',
        };
      case 'leaderboard_updated':
        return {
          message: 'Zaktualizowano ranking',
          icon: 'bar-chart',
        };
    }
  }

  // Conspectus (organ wzroku) — subjecty `tenant.*.events.nexus_eye.*`
  //
  // Ten blok MUSI stać przed ogólnym blokiem alertów niżej: `zagrozenie`
  // i `niezgodnosc_ubioru` nie zawierają słowa „alert" ani progu z wartością,
  // więc ogólna gałąź zamieniłaby je w „Alert: Zagrozenie" bez kamery, strefy
  // i bez powodu — czyli w komunikat, po którym nikt nie wie, gdzie iść.
  if (source === 'nexus_eye') {
    const kamera = getDataValue(d, 'kamera_nazwa');
    const strefa = getDataValue(d, 'strefa');
    const gdzie = [strefa, kamera].filter(Boolean).join(' / ') || 'kamera';
    const powod = getDataValue(d, 'uzasadnienie');

    switch (action) {
      case 'zagrozenie':
        return {
          message: `⚠️ Zagrożenie — ${gdzie}${powod ? `: ${powod}` : ''}`,
          icon: 'alert-triangle',
        };
      case 'niezgodnosc_ubioru': {
        const element = getDataValue(d, 'element');
        return {
          message: `Niezgodność ubioru — ${gdzie}${element ? `: ${String(element).replace(/_/g, ' ')}` : ''}`,
          icon: 'shield-alert',
        };
      }
      case 'odstepstwo_procesu':
        return {
          message: `Odstępstwo od procesu — ${gdzie}${powod ? `: ${powod}` : ''}`,
          icon: 'git-branch',
        };
      case 'kamera_offline':
        return {
          message: `Kamera przestała przysyłać obraz — ${gdzie}`,
          icon: 'video-off',
        };
      case 'kamera_online':
        return {
          message: `Kamera wróciła — ${gdzie}`,
          icon: 'video',
        };
      case 'gateway_milczy': {
        // Poziom KOMPUTERA z agentem, nie kamery — `gdzie` jest tu puste,
        // bo zdarzenie nie dotyczy jednej kamery, tylko wszystkich naraz.
        const komputer =
          getDataValue(d, 'host_nazwa') || getDataValue(d, 'gateway_nazwa') || 'agent kamer';
        const cisza = getDataValue(d, 'cisza_opis');
        const kamery = getDataValue(d, 'kamery_opis');
        return {
          message: `Kamery nie przysyłają obrazu — ${komputer} milczy${cisza ? ` od ${cisza}` : ''}${kamery ? ` (${kamery})` : ''}`,
          icon: 'video-off',
        };
      }
      case 'obserwacja':
        return {
          message: `Obserwacja — ${gdzie}`,
          icon: 'eye',
        };
    }
  }

  // ARTIFEX (organ ruchu) — subjecty `tenant.*.events.artifex.*`
  //
  // UWAGA NA RODZINĘ OBOK. `tenant.*.events.robot.>` to zdarzenia SPRZĘTOWE
  // publikowane przez Pythona (robot się zameldował, epizod się skończył)
  // i mają własne `source` — tamtej rodziny ten blok nie dotyczy i dotykać
  // nie może. Tu są DECYZJE ORGANU: co kurator orzekł, co wpięto do zbioru
  // uczącego, co dopuszczono na sprzęt.
  //
  // CZEGO W TYCH KOMUNIKATACH CELOWO NIE MA: `session_id`. Ładunek
  // `demonstration_linked` niesie go, bo serwis zwraca cały wiersz — ale kanał
  // zdarzeń ogląda administrator spoza najemcy, w którym nagranie powstało,
  // a od identyfikatora epizodu do nagrania konkretnego pracownika przy pracy
  // jest dokładnie jeden krok. To ta sama reguła, którą trzyma ekran zgód
  // ARTIFEKSA, i pęka tak samo cicho.
  if (source === 'artifex') {
    const robot = getDataValue(d, 'robot_type') ?? getNestedValue(d, 'command', 'robot_type');

    switch (action) {
      case 'demonstration_linked': {
        const modalnosc = getDataValue(d, 'modalnosc');
        return {
          message: `Demonstracja wpięta do zbioru uczącego${
            modalnosc ? ` — ${String(modalnosc).replace(/_/g, ' ')}` : ''
          }`,
          icon: 'graduation-cap',
        };
      }
      case 'demonstration_judged': {
        // `przydatna` bywa `null` (nieocenione) — dlatego porównanie wprost
        // do `true`, a nie zwykła prawdziwość: `null` ma trafić w gałąź
        // odrzucenia tylko wtedy, gdy naprawdę jest werdyktem. Czytane
        // z koperty, a nie przez `getDataValue`, bo tamten zwęża do
        // `string | number` i boolean by przez niego nie przeszedł.
        if (readEventPayload(d)['przydatna'] === true) {
          return { message: 'Kurator uznał ujęcie za przydatne', icon: 'check-circle' };
        }
        const powod = getDataValue(d, 'powod_kod');
        return {
          message: `Kurator odrzucił ujęcie${powod ? `: ${String(powod).replace(/_/g, ' ')}` : ''}`,
          icon: 'x-circle',
        };
      }
      case 'episode_requested': {
        const zadanie = getNestedValue(d, 'command', 'task_type');
        const sekund = getNestedValue(d, 'command', 'seconds');
        const opis = [robot, zadanie ? String(zadanie).replace(/_/g, ' ') : null]
          .filter(Boolean)
          .join(', ');
        return {
          message: `Zlecono przebieg symulacji${opis ? ` — ${opis}` : ''}${
            sekund !== undefined ? ` (${sekund} s)` : ''
          }`,
          icon: 'flask-conical',
        };
      }
      case 'training_requested': {
        // Kosztorys jest w komunikacie, bo to jedyna trasa organu, która
        // kosztuje pieniądze. Zlecenie na $12 nie może wyglądać w kanale
        // identycznie jak zlecenie na $1.
        const iteracji = getNestedValue(d, 'command', 'iteracji');
        const minut = getNestedValue(d, 'szacunek', 'minut');
        const koszt = getNestedValue(d, 'szacunek', 'koszt_usd');
        const opis = [robot, iteracji !== undefined ? `${iteracji} iteracji` : null]
          .filter(Boolean)
          .join(', ');
        const kosztorys = [
          minut !== undefined ? `~${minut} min` : null,
          koszt !== undefined ? `~$${koszt}` : null,
        ]
          .filter(Boolean)
          .join(', ');
        return {
          message: `Zlecono trening polityki${opis ? ` — ${opis}` : ''}${
            kosztorys ? ` (${kosztorys})` : ''
          }`,
          icon: 'cpu',
        };
      }
      case 'policy_admitted':
        return {
          message: `Polityka przeszła bramkę symulacyjną${robot ? ` — ${robot}` : ''}`,
          icon: 'shield-check',
        };
      case 'activity_level_raised': {
        const poziom = getDataValue(d, 'poziom_dojrzalosci') ?? getDataValue(d, 'poziom');
        return {
          message: `Czynność awansowała w katalogu zawodów${
            poziom ? `: ${String(poziom).replace(/_/g, ' ')}` : ''
          }`,
          icon: 'trending-up',
        };
      }
      case 'consent_withdrawn': {
        // Bez nazwiska i bez `worker_id`: wycofanie zgody jest faktem
        // o zbiorze uczącym, a nie wpisem do kartoteki człowieka.
        const zakres = getDataValue(d, 'zakres');
        return {
          message: `Wycofano zgodę na wykorzystanie nagrań${
            zakres ? ` (${zakres})` : ''
          } — demonstracje wypadają ze zbioru`,
          icon: 'shield-off',
        };
      }
    }
  }

  // Alert events
  if (
    category === 'alerts' ||
    action.includes('alert') ||
    action.includes('high_') ||
    action.includes('low_')
  ) {
    const value = getDataValue(d, 'value');
    const threshold = getDataValue(d, 'threshold');
    const device = deviceId ?? getDataValue(d, 'device_id');
    return {
      message: `⚠️ Alert: ${formatAction(action)}${device ? ` — ${device}` : ''}${value !== undefined ? ` (${value})` : ''}${threshold !== undefined ? ` próg: ${threshold}` : ''}`,
      icon: 'alert-triangle',
    };
  }

  // Tytuł zapasowy: najpierw zdanie z kontraktu zdarzenia (packages/types,
  // KONTRAKTY_ZDARZEN — po polsku, pisane przez organ, który zdarzenie zna),
  // dopiero potem humanizacja angielskiej nazwy akcji.
  const zKontraktu = tytulZKontraktu(source, action);
  return {
    message: zKontraktu ?? formatAction(action),
    icon: undefined,
  };
}
